import { useEffect, useMemo, useState } from "react";
import type { InstanceDetail, InstanceReset, InstanceResets } from "@keydials/api-client";
import { useWorkspaceServices } from "../context";
import { Icon } from "../components/Icon";
import {
  WEEKDAYS,
  clearedFieldLabels,
  draftFrom,
  draftProblem,
  formatResetTime,
  orderResets,
  recurrenceFromDraft,
  resetName,
  type ResetTimingDraft,
} from "./resetsModel";

/**
 * The dial's scheduled resets on its Overview (KEY-RESETTIME): when the next one runs, and — for a dial admin,
 * on a reset whose author allowed it — the day and time it runs on THIS dial.
 * <p>
 * Everyone on the dial sees the line, because a reset empties lists for all of them and "why did my list
 * empty?" deserves an answer on the page they're looking at. The Change control is the only part that is
 * gated. Renders nothing when the dial has no resets, when the server's reset sweep is in dry run (the list
 * comes back empty then), or when the list can't be loaded — this is a convenience, never a blocker.
 */
export function ResetsStrip({ detail }: { detail: InstanceDetail }) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [data, setData] = useState<InstanceResets | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const token = getAccessToken();
    if (!token) return;
    setData(null);
    setEditing(null);
    // Through a promise so even a synchronous throw (an older API client, a test double without the call)
    // lands in the catch below instead of taking the whole Overview down with it.
    Promise.resolve()
      .then(() => api.instanceResets(token, detail.id))
      .then((result) => { if (live) setData(result); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [api, getAccessToken, detail.id]);

  const resets = useMemo(() => orderResets(data?.resets ?? []), [data]);
  if (!data || resets.length === 0) return null;

  const zone = data.timeZoneId ?? null;
  const next = resets[0];
  const replace = (updated: InstanceReset) => {
    setData((current) => current && {
      ...current,
      resets: current.resets.map((r) => (addressOf(r) === addressOf(updated) ? updated : r)),
    });
    setEditing(null);
  };

  return (
    <div style={{ marginTop: 10, fontSize: 13 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", color: "var(--text-muted)" }}>
        <Icon name="clock" size={14} style={{ color: "var(--text-faint)", flex: "none" }} />
        <span>
          Next reset: <strong style={{ color: "var(--text)" }}>{resetName(detail, next)}</strong>
          {next.nextResetUtc && <> · {formatResetTime(next.nextResetUtc, zone)}</>}
        </span>
        <button
          type="button"
          className="kd-focusable"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{ border: 0, background: "transparent", padding: "2px 4px", cursor: "pointer", color: "var(--accent)", fontSize: 13, fontWeight: 600 }}
        >
          {open ? "Hide" : resets.some((r) => r.canChange) ? "All resets · change times" : `All resets (${resets.length})`}
        </button>
      </div>

      {open && (
        <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {resets.map((reset) => (
            <li key={addressOf(reset)} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px" }}>
              <ResetRow
                detail={detail}
                reset={reset}
                zone={zone}
                editing={editing === addressOf(reset)}
                onEdit={() => setEditing(addressOf(reset))}
                onCancel={() => setEditing(null)}
                onSaved={replace}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function addressOf(reset: Pick<InstanceReset, "sectionKey" | "segmentKey" | "itemKey" | "dimensionKey">): string {
  return [reset.sectionKey, reset.segmentKey, reset.itemKey ?? "", reset.dimensionKey ?? ""].join("\u0000");
}

function ResetRow({ detail, reset, zone, editing, onEdit, onCancel, onSaved }: {
  detail: InstanceDetail;
  reset: InstanceReset;
  zone: string | null;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSaved: (reset: InstanceReset) => void;
}) {
  const cleared = clearedFieldLabels(detail, reset.clearAttributeKeys);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ color: "var(--text)" }}>{resetName(detail, reset)}</strong>
        {reset.adjusted && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 99, padding: "0 7px" }}>
            This dial's own time
          </span>
        )}
        {reset.canChange && !editing && (
          <button type="button" className="kd-btn kd-btn-sm" style={{ marginLeft: "auto" }} onClick={onEdit}>
            Change
          </button>
        )}
      </div>
      <div style={{ color: "var(--text-muted)" }}>{reset.description}</div>
      <div style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
        {reset.nextResetUtc ? <>Next: {formatResetTime(reset.nextResetUtc, zone)}</> : "Next run unknown"}
        {cleared.length > 0 && <> · empties {cleared.join(", ")}</>}
      </div>
      {editing && <TimingEditor reset={reset} onCancel={onCancel} onSaved={onSaved} detailId={detail.id} />}
    </div>
  );
}

function TimingEditor({ reset, detailId, onCancel, onSaved }: {
  reset: InstanceReset;
  detailId: string;
  onCancel: () => void;
  onSaved: (reset: InstanceReset) => void;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [draft, setDraft] = useState<ResetTimingDraft>(() => draftFrom(reset.recurrence));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unit = reset.templateRecurrence.unit;
  const problem = draftProblem(unit, draft);

  const save = async (toDefault: boolean) => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.setInstanceResetTiming(token, detailId, {
        sectionKey: reset.sectionKey,
        segmentKey: reset.segmentKey,
        itemKey: reset.itemKey ?? null,
        dimensionKey: reset.dimensionKey ?? null,
        recurrence: toDefault ? null : recurrenceFromDraft(reset.templateRecurrence, draft),
      });
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "That couldn't be saved. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const label = { display: "flex", alignItems: "center", gap: 6, color: "var(--text-muted)", fontSize: 12.5 } as const;
  return (
    <div style={{ marginTop: 6, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 10 }}>
      {unit === "Week" && (
        <div role="group" aria-label="Days it runs" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {WEEKDAYS.map((day) => {
            const on = draft.days.includes(day);
            return (
              <button
                key={day}
                type="button"
                className="kd-focusable"
                aria-pressed={on}
                aria-label={day}
                onClick={() => setDraft((d) => ({ ...d, days: on ? d.days.filter((x) => x !== day) : [...d.days, day] }))}
                style={{
                  minWidth: 44, minHeight: 36, borderRadius: 99, cursor: "pointer", fontSize: 13, fontWeight: 600,
                  border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                  background: on ? "var(--accent)" : "transparent",
                  color: on ? "#fff" : "var(--text)",
                }}
              >
                {day.slice(0, 3)}
              </button>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {unit === "Month" && (
          <label style={label}>
            Day of the month
            <input
              className="field"
              type="number"
              min={1}
              max={31}
              value={draft.dayOfMonth}
              onChange={(e) => setDraft((d) => ({ ...d, dayOfMonth: Number(e.target.value) }))}
              style={{ width: 72 }}
            />
          </label>
        )}
        <label style={label}>
          At
          <input
            className="field"
            type="time"
            value={draft.time}
            onChange={(e) => setDraft((d) => ({ ...d, time: e.target.value }))}
            style={{ width: 120 }}
          />
        </label>
      </div>
      <div style={{ color: "var(--text-faint)", fontSize: 12.5, lineHeight: 1.45 }}>
        This changes when it runs for everyone on this dial. How often it runs and what it empties stay as the
        dial was set up.
      </div>
      {(error || problem) && (
        <div role="alert" style={{ color: "var(--st-red, #b91c1c)", fontSize: 12.5 }}>{error ?? problem}</div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" className="kd-btn kd-btn-primary kd-btn-sm" disabled={busy || problem !== null} onClick={() => void save(false)}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={onCancel}>Cancel</button>
        {reset.adjusted && (
          <button
            type="button"
            className="kd-focusable"
            disabled={busy}
            onClick={() => void save(true)}
            style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", color: "var(--accent)", fontSize: 12.5, fontWeight: 600 }}
          >
            Back to the dial's default ({reset.templateDescription})
          </button>
        )}
      </div>
    </div>
  );
}
